import * as React from 'react';
import { useTranslation } from 'next-i18next';
import styled, { css, keyframes } from 'styled-components';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { animated } from '@react-spring/web';

import { GoogleAutoCompleteInput, Heading } from '@restworld/ui-ds-base';
import { SkeletonTextLoading } from '@restworld/ui-components';
import { useAnalytics } from '@restworld/utils-common';
import { isOrgOnboardingModeAtom } from '../../organization-onboarding/use-organization-onboarding';
import {
  activeRestaurantOnboardingStateAtom,
  googlePlacesLastStatusAtom,
  googleSearchNoResultSeenAtom,
  isManualEntryAtom,
} from '.';
import { ManualEntryPrompt } from '../../shared/manual-venue-entry';
import { cn } from '@restworld/utils-common';

const GOOGLE_PLACES_ERROR_STATUSES = new Set([
  'OVER_QUERY_LIMIT',
  'REQUEST_DENIED',
  'INVALID_REQUEST',
  'UNKNOWN_ERROR',
  'NOT_FOUND',
]);

// How long a no-result query must stay unchanged before we emit the event. Long
// enough to skip mid-typing noise, short enough to still catch quick bouncers.
const NO_RESULT_SETTLE_MS = 2000;

const defaultInputProps = {
  type: 'text',
  id: '',
};

const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col w-full h-full gap-6 ;', className)} {...props} />
);

const inputAppear = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const inputPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(139, 92, 246, 0), 0 0 6px 0 rgba(139, 92, 246, 0.14);
  }
  50% {
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.14), 0 0 12px 1px rgba(139, 92, 246, 0.26);
  }
`;

const InputPulseWrapper = styled.div<{ $isPulsing: boolean }>`
  width: 100%;
  animation: ${inputAppear} 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;

  ${({ $isPulsing }) =>
    $isPulsing &&
    css`
      & input {
        animation: ${inputPulse} 2s ease-in-out 0.6s infinite;
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    & input {
      animation: none;
      box-shadow: 0 0 6px 0 rgba(139, 92, 246, 0.16);
    }
  }
`;

export const googleAddresInputValueAtom = atom('');
export const activeAddressIdAtom = atom('');
export const activeRestaurantAtom = atom('');
// Transient flag: when true, the Google address input mounts with autoFocus.
// Set by the intro CTA handler so the search input is focused immediately on
// transition from the intro screen (mobile keyboard opens).
export const googleAddressInputAutoFocusAtom = atom(false);

const GoogleAddressInputResult: React.FunctionComponent = () => {
  const [activeItem, setActiveItem] = useAtom(activeAddressIdAtom);
  const [inputValue, setInputValue] = useAtom(googleAddresInputValueAtom);
  const setActiveRestaurantName = useSetAtom(activeRestaurantAtom);
  const setShouldAutoFocus = useSetAtom(googleAddressInputAutoFocusAtom);
  const [lastStatus, setLastStatus] = useAtom(googlePlacesLastStatusAtom);

  const inputRef = React.useRef<HTMLDivElement | null>(null);
  const isInputFilled = inputValue.length > 0;

  // When Google has no usable result the dropdown holds a single "no results"
  // line, so drop its forced min-height — otherwise the 14rem floor leaves a big
  // empty gap and the manual-entry card no longer sits right under the message.
  const hasNoResult = lastStatus === 'ZERO_RESULTS' || GOOGLE_PLACES_ERROR_STATUSES.has(lastStatus);

  // Always focus the search input on mount so the user can start typing
  // immediately. Reset the transient atom flag after consuming it.
  React.useEffect(() => {
    setShouldAutoFocus(false);
    const inputEl = inputRef.current?.querySelector<HTMLInputElement>('input');
    inputEl?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToInput = React.useCallback((element: HTMLDivElement) => {
    const rect = element.getBoundingClientRect();
    const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

    if (!isVisible) {
      setTimeout(() => {
        if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.scrollTo({ top: rect.top + window.scrollY - 100, behavior: 'smooth' });
        } else {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 750);
    }
  }, []);

  React.useEffect(() => {
    if (isInputFilled && inputRef.current) scrollToInput(inputRef.current);
  }, [isInputFilled, scrollToInput]);

  const handleInputChange = React.useCallback(
    (value: string) => {
      setInputValue(value);
      if (activeItem) setActiveItem(null);
    },
    [activeItem, setActiveItem, setInputValue]
  );

  const handleDropdownItemClick = React.useCallback(
    (selectedItem: string, restaurantName?: string) => {
      setActiveItem(selectedItem);
      setActiveRestaurantName(restaurantName);
    },
    [setActiveItem, setActiveRestaurantName]
  );

  const inputProps = {
    ...defaultInputProps,
    icon: MagnifyingGlassIcon,
    bordercolor: 'primary' as const,
    hasLeftPadding: true,
    autoFocus: false,
    wrapperStyle: 'w-full',
    onChange: handleInputChange,
    value: inputValue,
    isFullwidth: true,
  };

  const dropdownItemProps = {
    onClick: handleDropdownItemClick,
    activeItem,
    itemRenderVariant: 'without_break_line' as const,
    loadingElement: (
      <li className="flex flex-col gap-4">
        <SkeletonTextLoading className="!w-full !h-[2.5rem]" width="rect-box" />
        <SkeletonTextLoading className="!w-full !h-[2.5rem]" width="rect-box" />
        <SkeletonTextLoading className="!w-full !h-[2.5rem]" width="rect-box" />
      </li>
    ),
  };

  return (
    <animated.div
      style={{
        transform: !isInputFilled ? 'translateY(4%)' : 'translateY(0%)',
        transition: 'transform 0.3s ease-in-out',
      }}
      className="w-full"
    >
      <Wrapper>
        {!isInputFilled && <OnboardingTitle />}
        <div className="flex flex-col gap-2 w-full">
          <InputPulseWrapper ref={inputRef} $isPulsing={!isInputFilled}>
            <GoogleAutoCompleteInput
              placesFilterTypes={['establishment']}
              formId="search-address-input"
              key="search-address-input"
              trackingSurface="employer_restaurant_onboarding_venue"
              placeholder="es. Pizzeria Mozzarella, Torino"
              inputProps={inputProps}
              dropdownItemProps={dropdownItemProps}
              mainWrapperStyle={hasNoResult ? 'w-full flex flex-col gap-2' : 'w-full flex flex-col gap-8'}
              dropdownMainWrapperStyle={'!relative border-none shadow-none lg:max-w-full lg:mt-0 !bg-transparent'}
              dropdownSubWrapperStyle={
                hasNoResult
                  ? 'max-h-full overflow-x-hidden px-0 py-0 min-h-0'
                  : 'max-h-full overflow-x-hidden px-0 py-0'
              }
              withFloatingUI={false}
              placesLocationBias={{
                south: 35.49,
                west: 6.63,
                north: 47.09,
                east: 18.52,
              }}
              onStatusChange={setLastStatus}
            />
          </InputPulseWrapper>
        </div>
        {isInputFilled && <ManualEntryLink />}
      </Wrapper>
    </animated.div>
  );
};

const OnboardingTitle = () => {
  const { t } = useTranslation('restaurant-onboarding');
  const isOrgOnboarding = useAtomValue(isOrgOnboardingModeAtom);

  return (
    <div className="self-start flex flex-col justify-start gap-1">
      <Heading size={4} color="neutral">
        {t('let_s_get_started')}
      </Heading>
      <Heading size={4} color="neutral">
        {t(isOrgOnboarding ? 'which_venue_are_you_looking_to_hire_staff_for' : 'search_your_restaurant_name')}
      </Heading>
      <p className="text-base font-normal text-neutral-500 m-0 mt-2">{t('let_s_get_started_subtitle')}</p>
    </div>
  );
};

const ManualEntryLink = () => {
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const setIsManualEntry = useSetAtom(isManualEntryAtom);
  const lastStatus = useAtomValue(googlePlacesLastStatusAtom);
  const lastQuery = useAtomValue(googleAddresInputValueAtom);
  const setNoResultSeen = useSetAtom(googleSearchNoResultSeenAtom);
  const { trackEvent } = useAnalytics();

  // A "no usable result" response from the Google Places API: either an explicit
  // ZERO_RESULTS, or an API-level error (quota exceeded, request denied, ...).
  const isZeroResults = lastStatus === 'ZERO_RESULTS';
  const isApiError = !!lastStatus && GOOGLE_PLACES_ERROR_STATUSES.has(lastStatus);
  const hasNoResult = isZeroResults || isApiError;

  // De-dupe per *distinct* query string (not once-per-session): the same failed
  // search is never reported twice, but every different venue name that returns
  // nothing is captured — a user who fails on 3 names is a stronger signal than one.
  const reportedNoResultQueriesRef = React.useRef<Set<string>>(new Set());

  // Fire when the Google Places API reports no results, independent of whether the
  // user goes on to click "enter manually". This measures how often a venue genuinely
  // can't be found via Google. We wait for the query to *settle* (NO_RESULT_SETTLE_MS
  // of no further changes) so mid-typing noise and partial gibberish don't fire:
  // every keystroke/status change re-runs this effect and clears the pending timer,
  // so we only emit once the user has paused on a query Google found nothing for.
  React.useEffect(() => {
    const query = (lastQuery || '').trim();
    if (!hasNoResult || query.length < 3) return;
    if (reportedNoResultQueriesRef.current.has(query)) return;

    const timer = setTimeout(() => {
      reportedNoResultQueriesRef.current.add(query);
      const reason = isZeroResults ? 'zero_results' : 'api_error';
      trackEvent('employer_onboarding_google_search_no_result', {
        trigger: 'gmaps_api',
        reason,
        ...(reason === 'api_error' ? { error_code: lastStatus } : {}),
        last_query: query,
      });
      setNoResultSeen(true);
    }, NO_RESULT_SETTLE_MS);

    return () => clearTimeout(timer);
  }, [hasNoResult, isZeroResults, lastStatus, lastQuery, trackEvent, setNoResultSeen]);

  const handleClick = React.useCallback(() => {
    if (hasNoResult) {
      const reason = isZeroResults ? 'zero_results' : 'api_error';
      trackEvent('employer_onboarding_google_search_no_result', {
        trigger: 'manual_entry_click',
        reason,
        ...(reason === 'api_error' ? { error_code: lastStatus } : {}),
        last_query: lastQuery,
      });
      setNoResultSeen(true);
    }
    setIsManualEntry(true);
    setActiveUI('restaurant_onboarding_form');
  }, [setActiveUI, setIsManualEntry, hasNoResult, isZeroResults, lastStatus, lastQuery, setNoResultSeen, trackEvent]);

  // Promote the manual-entry prompt to a high-affordance card when Google has no match.
  return <ManualEntryPrompt visible emphasized={hasNoResult} onClick={handleClick} />;
};

export default GoogleAddressInputResult;
